/* What the Policies section says about a policy (a rule, on the wire) — its channels and
   stages by name, its conditions as chips, its effect in a line, a person's name. No React
   here. */
import { DEFAULT_DAILY_BUDGET, DEFAULT_MESSAGE_LIMIT } from "@/shared/types";

import type { AgentRow } from "@/shared/types";
import type { Rule, RuleAction, RuleChannel, RuleStatus, RuleWhen } from "../../types";

export const CHANNELS: [RuleChannel, string][] = [
  ["web", "Client app"], ["extension", "Extension"], ["api", "API"], ["whatsapp", "WhatsApp"], ["telegram", "Telegram"], ["slack", "Slack"], ["discord", "Discord"], ["teams", "Microsoft Teams"],
];
export const CHANNEL_NAME = Object.fromEntries(CHANNELS) as Record<RuleChannel, string>;

export const STAGES: [RuleStatus, string][] = [["onboarding", "Still onboarding"], ["active", "Finished"]];
const STAGE_SAID: Record<RuleStatus, string> = { onboarding: "Still onboarding", active: "Finished onboarding" };

/** The default refusal, as the service words it to a blocked person. */
export const DEFAULT_REFUSAL = "Sorry, this assistant isn't available to you.";

/** Everyone else: the agent's own limits. A private agent without a budget has none; a shared
    one has the default. */
export interface Fallback {
  budget: number | null;
  msgs: number;
}
export const fallbackOf = (row: AgentRow): Fallback => ({
  budget: row.daily_budget_usd ?? (row.visibility === "private" ? null : DEFAULT_DAILY_BUDGET),
  msgs: row.message_limit ?? DEFAULT_MESSAGE_LIMIT,
});

export const money = (v: number | null) => (v === null ? "no daily budget" : `$${v} a day`);
export const perTen = (n: number) => `${n} messages per person every 10 min`;

/** What it lets them do or tells them: "$5 a day · 20 messages per person every 10 min". */
export function effectOf(p: Pick<Rule, "action" | "daily_budget_usd" | "message_limit" | "message"> | null, fb: Fallback): string {
  if (p?.action === "block") return `Told: ${p.message || DEFAULT_REFUSAL}`;
  return [money(p?.daily_budget_usd ?? fb.budget), perTen(p?.message_limit ?? fb.msgs)].join(" · ");
}

/** An allow policy's own limits, or what it falls back to. */
export function ownLimitsOf(p: Rule): string {
  const own = [
    p.daily_budget_usd != null ? money(p.daily_budget_usd) : null,
    p.message_limit != null ? `${p.message_limit} messages every 10 min` : null,
  ].filter(Boolean);
  return own.length ? own.join(" · ") : "Everyone else's limits";
}

/** Its conditions as the chips its card shows; a condition still being written says so. */
export function chipsOf(when: RuleWhen, questionLabel: (key: string) => string | undefined): { text: string; empty: boolean }[] {
  const chips: { text: string; empty: boolean }[] = [];
  const add = (text: string, empty = false) => chips.push({ text, empty });
  if (when.channels) (when.channels.length ? when.channels.forEach((c) => add(CHANNEL_NAME[c])) : add("No channel picked", true));
  if (when.people) (when.people.length ? when.people.forEach((e) => add(e)) : add("No email or domain yet", true));
  if (when.answer) {
    const { key, value } = when.answer;
    add(`${(key && questionLabel(key)) || "An answer"} includes ${value ? `“${value}”` : "…"}`, !key || !value);
  }
  if (when.status) {
    if (when.status.length) add(when.status.map((s) => STAGE_SAID[s]).join(" or "));
    else add("No stage picked", true);
  }
  return chips;
}

export const ACTIONS: { value: RuleAction; label: string }[] = [
  { value: "allow", label: "Let them in" },
  { value: "block", label: "Keep them out" },
];

/** A person's name as a list shows it. */
export const personName = (p: { name: string | null; email: string | null; channels: string[] }) =>
  (p.name && p.name !== "Unknown" ? p.name : null) ?? p.email ?? `${CHANNEL_NAME[p.channels[0] as RuleChannel] ?? "Someone"} user`;

export const initialsOf = (name: string) =>
  name.replace(/^\+/, "").split(/[\s@.]+/).filter(Boolean).slice(0, 2).map((w) => w[0].toUpperCase()).join("") || "?";
